import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box } from '@mui/material';

import CommentLayer from '@/designs-dev/comments/CommentLayer';
import { usePhoneScreen } from '@/designs-dev/device/PhoneScreenContext';
import DirectEdit from '@/designs-dev/inspect/DirectEdit';
import { REGISTRY } from '@/designs-dev/inspect/ds-registry';
import { useInspect } from '@/designs-dev/inspect/InspectContext';
import { useInspectUrlSync } from '@/designs-dev/inspect/useInspectUrlSync';
import {
  baseName,
  buildLayerTree,
  inspectNode,
  type LayerNode,
} from '@/designs-dev/inspect/fiber';
import { useTweaks } from '@/designs-dev/inspect/tweaks';

// The direct-edit handles sit inside the screen, so the drill listeners have to
// tell a drag on a handle from a click on the design.
const onHandle = (e: Event) =>
  !!(e.target as HTMLElement | null)?.closest?.('[data-drill-handle]');

type Rect = { top: number; left: number; width: number; height: number };

// Lives inside PhoneFrame (needs PhoneScreenContext). When inspect mode is on it
// reads the component under the cursor (passively — no pointer capture) and draws
// a highlight box around the current selection. The selection can also come from
// the layers panel — the highlight is derived from `current`, not the cursor.
// Click pins the current selection so you can read the panel.
const DrillOverlay = ({
  screenEl,
  editable,
}: {
  screenEl: HTMLElement | null;
  editable: boolean;
}) => {
  const { active, current, setCurrent, hovered, setHovered, setScreenEl } =
    useInspect();
  const raf = useRef<number | undefined>(undefined);
  // Re-measure when a tweak lands: a drag changes the geometry every box here is
  // drawn from.
  useTweaks();

  // Drill mode + the pinned selection live in the URL (?drill / ?select), so a
  // drilled view is a shareable address.
  useInspectUrlSync(screenEl);

  // Register the screen element so panels outside the frame (layers) can walk
  // the same tree.
  useEffect(() => {
    setScreenEl(screenEl);
    return () => setScreenEl(null);
  }, [screenEl, setScreenEl]);

  useEffect(() => {
    if (!active || !screenEl) return;

    const measure = (clientX: number, clientY: number) =>
      inspectNode(
        document.elementFromPoint(clientX, clientY) as HTMLElement | null,
      );

    // Hover only previews — it never touches the committed selection.
    const onMove = (e: MouseEvent) => {
      if (onHandle(e)) return;
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() =>
        setHovered(measure(e.clientX, e.clientY)),
      );
    };
    const onLeave = () => setHovered(null);
    // Click commits (or, on empty space, clears) the selection.
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // The click that ends a drag on a handle keeps the selection it was made on.
      if (onHandle(e)) return;
      setCurrent(measure(e.clientX, e.clientY));
    };

    // The click is swallowed below, so nothing actions. But the screen still
    // looked operable: pointer cursors, hover fills and llama's :active press.
    // Flagging the screen lets one stylesheet rule take the press affordance
    // away, so the phone reads as something you pick layers out of.
    screenEl.dataset.drill = 'on';

    screenEl.addEventListener('mousemove', onMove);
    screenEl.addEventListener('mouseleave', onLeave);
    screenEl.addEventListener('click', onClick, true);
    return () => {
      screenEl.removeEventListener('mousemove', onMove);
      screenEl.removeEventListener('mouseleave', onLeave);
      screenEl.removeEventListener('click', onClick, true);
      delete screenEl.dataset.drill;
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [active, screenEl, setCurrent, setHovered]);

  // A scale-aware DOM-node → screen-rect mapper. The screen may be CSS-scaled
  // (ComputerFrame renders 1440×900 scaled to fit); bounding rects are in
  // visual pixels but the portal target's space is logical, so divide the
  // scale back out. Guard against nodes that have since unmounted.
  let toRect: ((el: HTMLElement) => Rect) | null = null;
  let scale = 1;
  if (active && screenEl) {
    const s = screenEl.getBoundingClientRect();
    scale = screenEl.offsetWidth ? s.width / screenEl.offsetWidth : 1;
    toRect = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return {
        top: (r.top - s.top) / scale,
        left: (r.left - s.left) / scale,
        width: r.width / scale,
        height: r.height / scale,
      };
    };
  }
  const rectFor = (insp: typeof current): Rect | null =>
    toRect && insp?.domNode && screenEl?.contains(insp.domNode)
      ? toRect(insp.domNode)
      : null;

  const currentRect = rectFor(current);
  // Preview the hovered component, unless it's already the committed one.
  const hoveredRect =
    hovered?.domNode !== current?.domNode ? rectFor(hovered) : null;

  // Every component inside the committed selection gets an ever-so-slight echo,
  // so the composition reads at a glance. Deduped by geometry so piped wrappers
  // don't double up.
  const childRects: Rect[] = [];
  if (currentRect && toRect && current?.domNode && screenEl) {
    const seen = new Set<string>([
      [currentRect.top, currentRect.left, currentRect.width, currentRect.height]
        .map((n) => Math.round(n))
        .join(','),
    ]);
    const collect = (nodes: LayerNode[]) => {
      for (const n of nodes) {
        const el = n.inspection.domNode;
        if (el && el !== current.domNode && screenEl.contains(el)) {
          const r = toRect!(el);
          const key = [r.top, r.left, r.width, r.height]
            .map((v) => Math.round(v))
            .join(',');
          if (!seen.has(key)) {
            seen.add(key);
            childRects.push(r);
          }
        }
        collect(n.children);
      }
    };
    collect(buildLayerTree(current.domNode));
  }

  if (!screenEl || (!currentRect && !hoveredRect)) return null;

  // The same file the panel points at, so a handle's instruction names it too.
  const currentFile =
    current?.file ??
    (current && !current.muiBase
      ? REGISTRY[baseName(current.name)]?.file
      : undefined);

  return createPortal(
    <>
      {childRects.map((r, i) => (
        <Box
          key={i}
          data-drill-layer
          sx={{
            position: 'absolute',
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
            pointerEvents: 'none',
            zIndex: 29,
            border: '1px solid',
            borderColor: 'primary.main',
            opacity: 0.3,
            borderRadius: '3px',
            backgroundColor: (theme) => theme.palette.primary.main + '0A',
          }}
        />
      ))}
      {/* hover preview — light, doesn't commit anything */}
      {hoveredRect && (
        <Box
          data-drill-layer
          sx={{
            position: 'absolute',
            top: hoveredRect.top,
            left: hoveredRect.left,
            width: hoveredRect.width,
            height: hoveredRect.height,
            pointerEvents: 'none',
            zIndex: 30,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: `${radius.sm}px`,
            backgroundColor: (theme) => theme.palette.primary.main + '14',
            transition: 'all 80ms linear',
          }}
        />
      )}
      {/* committed selection — strong coral, set only on click */}
      {currentRect && (
        <Box
          data-drill-layer
          sx={{
            position: 'absolute',
            top: currentRect.top,
            left: currentRect.left,
            width: currentRect.width,
            height: currentRect.height,
            pointerEvents: 'none',
            zIndex: 31,
            border: '2px solid',
            borderColor: 'custom.coral.600',
            borderRadius: `${radius.sm}px`,
            transition: 'all 80ms linear',
          }}
        />
      )}
      {editable && current && currentRect && toRect && (
        <DirectEdit
          current={current}
          file={currentFile}
          toRect={toRect}
          scale={scale}
        />
      )}
    </>,
    screenEl,
  );
};

// Every stage mounts this inside its screen, so it carries comment mode's pins
// as well as the drill highlights.
// `editable` false for a page that is only read, like the component library:
// it selects and explains, with no handles to drag.
const InspectOverlay = ({ editable = true }: { editable?: boolean }) => {
  const screenEl = usePhoneScreen();
  return (
    <>
      <DrillOverlay screenEl={screenEl} editable={editable} />
      <CommentLayer screenEl={screenEl} />
    </>
  );
};

export default InspectOverlay;
